import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ATIVIDADES_PRESUNCAO,
  formatarDecimal,
  Premissas,
  PremissasPlanejamento,
  ROTULO_ATIVIDADE_PRESUNCAO,
  type ResumoOperacoes,
} from "@via-tax/contracts";
import { Aviso, Botao, Campo, Cartao, Selecao } from "@via-tax/ui";
import { useState, type ReactNode } from "react";
import { reais } from "../formatos";
import { mensagemDeErro, useTRPC } from "../trpc";
import { EntradaNumero } from "./EntradaNumero";

/**
 * Hook: botão de rodar a simulação + painel ÚNICO de premissas, compartilhado por todas as telas de simulação.
 * O painel separa o que a regra decide (lei e CFOP, com os valores da última simulação) do que o escritório
 * escolhe. Parte das premissas da última simulação: rodar de uma tela não desfaz o que foi escolhido em outra.
 */

export type SecaoPremissas = "vendas" | "compras" | "preco" | "planejamento";

type Form = {
  regimeFornecedorDesconhecido: Premissas["regimeFornecedorDesconhecido"];
  rbt12FornecedorSimples: number;
  creditoUsoConsumo: boolean;
  anualizar: boolean;
  repasseComCredito: number;
  repasseSemCredito: number;
  vendaCreditoIncerto: Premissas["vendaCreditoIncerto"];
  planejamento: PremissasPlanejamento;
};

const formDe = (p: Premissas): Form => ({
  regimeFornecedorDesconhecido: p.regimeFornecedorDesconhecido,
  rbt12FornecedorSimples: p.rbt12FornecedorSimples,
  creditoUsoConsumo: p.creditoUsoConsumo ?? true,
  anualizar: p.anualizar,
  repasseComCredito: p.repasseComCredito ?? p.repasse,
  repasseSemCredito: p.repasseSemCredito ?? p.repasse,
  vendaCreditoIncerto: p.vendaCreditoIncerto,
  planejamento: p.planejamento ?? PremissasPlanejamento.parse({}),
});
const PADRAO = formDe(Premissas.parse({}));

const pct = (ppm: number) => `${formatarDecimal(ppm, 4)}%`;
const ROTULO_FORNECEDOR = {
  SN_PURO: "Simples puro",
  SN_HIBRIDO: "Simples híbrido",
  REGULAR: "regime regular",
} as const;

const POLITICAS = [
  { rotulo: "Cliente paga o mesmo que hoje", empresas: 0, consumidor: 0 },
  {
    rotulo: "Empresas pagam o tributo, consumidor paga o mesmo",
    empresas: 1_000_000,
    consumidor: 0,
  },
  {
    rotulo: "Mantém o preço para todos",
    empresas: 1_000_000,
    consumidor: 1_000_000,
  },
];

export function useExecutarSimulacao({
  premissasAtuais,
  processando,
  jaRodou,
  simples,
  operacoes,
  secao: secaoInicial = "vendas",
  abertoInicial = false,
}: {
  premissasAtuais: unknown;
  processando: boolean;
  jaRodou: boolean;
  simples: boolean;
  /** resumo da última simulação: mostra, ao lado de cada regra, quanto ela pegou */
  operacoes?: ResumoOperacoes | null;
  secao?: SecaoPremissas;
  abertoInicial?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const inicial = Premissas.safeParse(premissasAtuais ?? {});
  const p = inicial.success ? inicial.data : Premissas.parse({});
  const [aberto, setAberto] = useState(abertoInicial);
  const [secao, setSecao] = useState<SecaoPremissas>(
    secaoInicial === "preco" && !simples ? "vendas" : secaoInicial,
  );
  // A última simulação chega depois do primeiro render (e muda ao rodar de novo): o formulário acompanha.
  const chave = JSON.stringify(p);
  const [estado, setEstado] = useState({ chave, form: formDe(p) });
  if (estado.chave !== chave) setEstado({ chave, form: formDe(p) });
  const f = estado.form;
  const mudar = (parcial: Partial<Form>) =>
    setEstado((e) => ({ ...e, form: { ...e.form, ...parcial } }));
  const mudarPlan = (parcial: Partial<PremissasPlanejamento>) =>
    setEstado((e) => ({
      ...e,
      form: { ...e.form, planejamento: { ...e.form.planejamento, ...parcial } },
    }));
  const alterado = JSON.stringify(f) !== JSON.stringify(formDe(p));
  const executar = useMutation(
    trpc.simulacao.executar.mutationOptions({
      onSuccess: () =>
        qc.invalidateQueries({ queryKey: trpc.simulacao.pathKey() }),
    }),
  );

  const rodar = () =>
    executar.mutate({
      premissas: {
        regimeFornecedorDesconhecido: f.regimeFornecedorDesconhecido,
        rbt12FornecedorSimples: f.rbt12FornecedorSimples,
        creditoUsoConsumo: f.creditoUsoConsumo,
        anualizar: f.anualizar,
        // `repasse` fica igual ao do consumidor para quem ainda lê só o campo antigo.
        repasse: f.repasseSemCredito,
        repasseComCredito: f.repasseComCredito,
        repasseSemCredito: f.repasseSemCredito,
        vendaCreditoIncerto: f.vendaCreditoIncerto,
        planejamento: f.planejamento,
      },
    });

  const pl = f.planejamento;
  const resumo = [
    `fornecedor sem regime conhecido: ${ROTULO_FORNECEDOR[f.regimeFornecedorDesconhecido]}`,
    `uso e consumo ${f.creditoUsoConsumo ? "com" : "sem"} crédito`,
    ...(simples
      ? [
          `repasse no híbrido: empresas ${pct(f.repasseComCredito)}, consumidor ${pct(f.repasseSemCredito)}`,
        ]
      : []),
    `folha ${reais(pl.folhaAnual)} · pró-labore ${reais(pl.proLaboreAnual)}`,
    f.anualizar ? "anualiza" : "sem anualizar",
  ];
  const abas: Array<{ id: SecaoPremissas; rotulo: string }> = [
    { id: "vendas", rotulo: "Vendas · débitos" },
    { id: "compras", rotulo: "Compras · créditos" },
    ...(simples ? [{ id: "preco" as const, rotulo: "Preço no híbrido" }] : []),
    { id: "planejamento", rotulo: "Planejamento de regime" },
  ];
  const ops = operacoes ?? null;

  return {
    botoes: (
      <div className="flex flex-wrap gap-2">
        <Botao
          variante={aberto ? "secundario" : "fantasma"}
          onClick={() => setAberto((a) => !a)}
          aria-expanded={aberto}
        >
          {aberto ? "Fechar premissas" : "Premissas"}
          {alterado && (
            <span
              className="size-2 rounded-full bg-acao"
              aria-label="alteradas"
            />
          )}
        </Botao>
        <Botao
          variante="primario"
          onClick={rodar}
          disabled={executar.isPending || processando}
        >
          {processando
            ? "Simulando…"
            : jaRodou
              ? "Simular de novo"
              : "Rodar simulação"}
        </Botao>
      </div>
    ),
    painel: (
      <>
        {aberto ? (
          <Cartao
            titulo="Premissas da simulação"
            acoes={
              <button
                type="button"
                className="text-xs text-navy underline"
                onClick={() =>
                  mudar({
                    ...PADRAO,
                    planejamento: {
                      ...PADRAO.planejamento,
                      folhaAnual: pl.folhaAnual,
                      proLaboreAnual: pl.proLaboreAnual,
                      despesasOperacionaisAnuais: pl.despesasOperacionaisAnuais,
                      lucroAntesIrInformado: pl.lucroAntesIrInformado,
                    },
                  })
                }
              >
                Voltar ao padrão
              </button>
            }
          >
            <p className="mb-4 max-w-3xl text-[13px] text-tinta-2">
              Em cada aba, à esquerda está <strong>o que a regra decide</strong>{" "}
              (lei e CFOP das notas — não muda por empresa) e à direita{" "}
              <strong>o que o escritório escolhe</strong>. As escolhas valem ao
              clicar em {jaRodou ? '"Simular de novo"' : '"Rodar simulação"'} e
              ficam gravadas com a simulação.
            </p>
            <div
              role="tablist"
              className="mb-4 flex flex-wrap gap-1 border-b border-linha"
            >
              {abas.map((a) => (
                <button
                  key={a.id}
                  role="tab"
                  type="button"
                  aria-selected={secao === a.id}
                  onClick={() => setSecao(a.id)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm ${secao === a.id ? "border-navy font-medium text-navy" : "border-transparent text-tinta-2 hover:text-tinta"}`}
                >
                  {a.rotulo}
                </button>
              ))}
            </div>

            {secao === "vendas" && (
              <Duas
                regras={
                  <>
                    <Regra
                      tipo="entra"
                      titulo="Vendas, serviços e fretes autorizados"
                      valor={ops ? reais(ops.vendas) : undefined}
                    >
                      NF-e, NFC-e, NFS-e e CT-e das 12 competências fechadas
                      mais recentes. Nota cancelada ou denegada não entra.
                    </Regra>
                    <Regra
                      tipo="abate"
                      titulo="Devoluções de venda"
                      valor={ops ? `−${reais(ops.abatidoVendas)}` : undefined}
                    >
                      CFOP x.201, x.202, x.410, x.411 e anulações: abatem a
                      receita do próprio mês, com os tributos destacados na
                      mesma proporção.
                    </Regra>
                    <Regra
                      tipo="fora"
                      titulo="O que não é venda"
                      valor={ops ? reais(ops.foraVendas) : undefined}
                    >
                      Remessas e retornos (conserto, industrialização,
                      demonstração, consignação), transferências, bonificações,
                      amostras e venda de ativo imobilizado (CFOP x.15x, x.9xx,
                      5.551). Devolução de compra emitida abate as compras.
                    </Regra>
                    {simples ? (
                      <Regra tipo="info" titulo="Como vira débito">
                        Hoje e no Simples puro: DAS pela tabela do anexo; venda
                        com ICMS por substituição (CST 60, CSOSN 500) tira a
                        parcela do ICMS do DAS (LC 123, art. 18 § 4º-A). No
                        híbrido: CBS e IBS por fora do DAS, com a redução do
                        produto ou do perfil de serviço.
                      </Regra>
                    ) : (
                      <Regra tipo="info" titulo="Como vira débito">
                        Hoje: ICMS, ISS, PIS/COFINS e IPI destacados nas notas.
                        De 2027 em diante: CBS e IBS sobre o valor sem
                        PIS/COFINS e sem o ICMS/ISS que ainda existe no ano, com
                        a redução do produto (catálogo) ou do perfil de serviço.
                      </Regra>
                    )}
                    <Regra tipo="info" titulo="Quem compra de você">
                      CPF ou sem documento = consumidor final. CNPJ do regime
                      regular ou do Simples híbrido = empresa que se credita,
                      mesmo em NFC-e. Simples puro, MEI e imune = sem crédito.
                      Regime não confirmado: a premissa ao lado.
                    </Regra>
                  </>
                }
              >
                {simples && (
                  <Campo
                    rotulo="Cliente CNPJ com regime ainda não confirmado"
                    dica="Só pesa na conta do híbrido: define se essa venda vai para o grupo das empresas que se creditam."
                  >
                    <Selecao
                      value={f.vendaCreditoIncerto}
                      onChange={(e) =>
                        mudar({
                          vendaCreditoIncerto: e.target
                            .value as Form["vendaCreditoIncerto"],
                        })
                      }
                    >
                      <option value="SEM_CREDITO">
                        Conta como empresa sem crédito (conservador)
                      </option>
                      <option value="COM_CREDITO">
                        Conta como empresa que se credita
                      </option>
                    </Selecao>
                  </Campo>
                )}
                <p className="text-xs font-medium text-tinta-2">
                  {simples
                    ? "Para simular Presumido e Real (a nota do Simples não mostra ICMS/ISS próprios)"
                    : "Quando as notas não trazem ICMS ou ISS destacados"}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Campo
                    rotulo="ICMS das vendas"
                    dica="Sobre vendas com ICMS próprio; ST, isentas e não tributadas ficam fora."
                  >
                    <EntradaNumero
                      tipo="percentual"
                      valor={pl.icmsVendas}
                      min={0}
                      max={1_000_000}
                      onValor={(v) => mudarPlan({ icmsVendas: v ?? 0 })}
                    />
                  </Campo>
                  <Campo
                    rotulo="ISS dos serviços"
                    dica="De 2,00% a 5,00% conforme o município."
                  >
                    <EntradaNumero
                      tipo="percentual"
                      valor={pl.iss}
                      min={0}
                      max={1_000_000}
                      onValor={(v) => mudarPlan({ iss: v ?? 0 })}
                    />
                  </Campo>
                </div>
              </Duas>
            )}

            {secao === "compras" && (
              <Duas
                regras={
                  <>
                    <Regra
                      tipo="entra"
                      titulo="Compras de mercadoria e serviços tomados"
                      valor={ops ? reais(ops.compras) : undefined}
                    >
                      NF-e, NFS-e e CT-e recebidos, das mesmas competências das
                      vendas.
                    </Regra>
                    <Regra
                      tipo="abate"
                      titulo="Devoluções de compra"
                      valor={ops ? `−${reais(ops.abatidoCompras)}` : undefined}
                    >
                      CFOP x.202, x.411, 5.553 e 5.556 emitidos: abatem as
                      compras e o crédito do próprio mês.
                    </Regra>
                    <Regra
                      tipo="fora"
                      titulo="O que não é compra"
                      valor={ops ? reais(ops.foraCompras) : undefined}
                    >
                      Retornos, remessas recebidas, transferências, bonificações
                      e amostras (CFOP x.15x, x.9xx) e devoluções de venda —
                      essas abatem as vendas.
                    </Regra>
                    {simples ? (
                      <Regra tipo="info" titulo="Crédito da empresa do Simples">
                        Hoje e no Simples puro não há crédito (o DAS é sobre a
                        receita). No híbrido, credita CBS e IBS das compras como
                        o regime regular, pelas regras abaixo.
                      </Regra>
                    ) : (
                      <Regra tipo="info" titulo="Crédito de hoje">
                        ICMS: só revenda e insumo, pelo destacado no XML, sem os
                        itens com substituição tributária (CST 10/30/70, CSOSN
                        201–203), somado ao crédito do fornecedor do Simples
                        (vCredICMSSN). PIS/COFINS: só no Lucro Real, revenda e
                        insumo, 9,25% sobre o valor sem ICMS. IPI: indústria,
                        nos insumos.
                      </Regra>
                    )}
                    <Regra
                      tipo="info"
                      titulo="Crédito de CBS e IBS (2027 em diante)"
                    >
                      Fornecedor do regime regular ou híbrido: crédito cheio,
                      inclusive ativo imobilizado, serviços, energia e frete (LC
                      214, art. 47). Fornecedor do Simples puro: só a CBS e o
                      IBS que estão no DAS dele. MEI, pessoa física e produtor
                      rural não contribuinte: sem crédito. Monofásico e
                      diferimento: sem crédito.
                    </Regra>
                    <Regra tipo="info" titulo="Destinação da compra">
                      Revenda, insumo, uso e consumo ou ativo vem do catálogo de
                      produtos (perfil aprovado ou sugestão); sem catálogo, do
                      CFOP da nota de entrada.
                    </Regra>
                  </>
                }
              >
                <Campo
                  rotulo="Fornecedor com regime desconhecido"
                  dica="Vale até o regime ser confirmado em Fornecedores. Simples puro é o conservador."
                >
                  <Selecao
                    value={f.regimeFornecedorDesconhecido}
                    onChange={(e) =>
                      mudar({
                        regimeFornecedorDesconhecido: e.target
                          .value as Form["regimeFornecedorDesconhecido"],
                      })
                    }
                  >
                    <option value="SN_PURO">
                      Simples puro — crédito só do que está no DAS
                    </option>
                    <option value="SN_HIBRIDO">
                      Simples híbrido — crédito cheio
                    </option>
                    <option value="REGULAR">
                      Regime regular — crédito cheio
                    </option>
                  </Selecao>
                </Campo>
                <Campo
                  rotulo="Faturamento de 12 meses do fornecedor do Simples puro"
                  dica="Define a faixa da tabela e, com ela, quanto de CBS/IBS está no DAS e passa como crédito."
                >
                  <EntradaNumero
                    tipo="reais"
                    valor={f.rbt12FornecedorSimples}
                    min={1}
                    max={480_000_000}
                    onValor={(v) =>
                      mudar({
                        rbt12FornecedorSimples:
                          v ?? PADRAO.rbt12FornecedorSimples,
                      })
                    }
                  />
                </Campo>
                <Campo
                  rotulo="Compras de uso e consumo"
                  dica={`Material de escritório, limpeza, manutenção. A lei dá crédito, exceto bens de uso pessoal (LC 214, art. 57).${ops ? ` No ano-base: ${reais(ops.usoConsumo)}.` : ""}`}
                >
                  <Selecao
                    value={f.creditoUsoConsumo ? "SIM" : "NAO"}
                    onChange={(e) =>
                      mudar({ creditoUsoConsumo: e.target.value === "SIM" })
                    }
                  >
                    <option value="SIM">
                      Dão crédito de CBS/IBS (LC 214, art. 47)
                    </option>
                    <option value="NAO">Sem crédito (conservador)</option>
                  </Selecao>
                </Campo>
              </Duas>
            )}

            {secao === "preco" && simples && (
              <Duas
                regras={
                  <>
                    <Regra tipo="info" titulo="O que é o repasse">
                      No híbrido, a CBS e o IBS saem do DAS e vão destacados na
                      nota. Repasse de 0,00% = o cliente paga o mesmo total que
                      hoje (o preço sem tributo cai). 100,00% = você mantém o
                      preço de hoje e soma a CBS/IBS por fora.
                    </Regra>
                    <Regra tipo="info" titulo="Por que separar por cliente">
                      A empresa que se credita recupera a CBS/IBS destacada:
                      pagar o tributo por fora não a encarece. O consumidor
                      final e a empresa sem crédito pagam de verdade.
                    </Regra>
                  </>
                }
              >
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-tinta-2">
                    Atalhos
                  </span>
                  {POLITICAS.map((x) => (
                    <button
                      key={x.rotulo}
                      type="button"
                      onClick={() =>
                        mudar({
                          repasseComCredito: x.empresas,
                          repasseSemCredito: x.consumidor,
                        })
                      }
                      className={`rounded-md border px-3 py-1.5 text-left text-[13px] ${f.repasseComCredito === x.empresas && f.repasseSemCredito === x.consumidor ? "border-navy bg-navy/5 text-navy" : "border-linha hover:bg-fundo"}`}
                    >
                      {x.rotulo}
                    </button>
                  ))}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Campo rotulo="Repasse a empresas que se creditam">
                    <EntradaNumero
                      tipo="percentual"
                      valor={f.repasseComCredito}
                      min={0}
                      max={1_000_000}
                      onValor={(v) => mudar({ repasseComCredito: v ?? 0 })}
                    />
                  </Campo>
                  <Campo rotulo="Repasse a consumidor e empresas sem crédito">
                    <EntradaNumero
                      tipo="percentual"
                      valor={f.repasseSemCredito}
                      min={0}
                      max={1_000_000}
                      onValor={(v) => mudar({ repasseSemCredito: v ?? 0 })}
                    />
                  </Campo>
                </div>
              </Duas>
            )}

            {secao === "planejamento" && (
              <Duas
                regras={
                  <>
                    <Regra tipo="info" titulo="Para que serve">
                      Só a comparação Simples × Presumido × Real usa estes
                      dados: as notas não mostram folha, pró-labore nem
                      despesas.
                    </Regra>
                    <Regra tipo="info" titulo="Presunção do Lucro Presumido">
                      Por tipo de receita: mercadoria 8% IRPJ e 12% CSLL,
                      transporte pela CT-e, serviço pelo perfil da empresa
                      (saúde hospitalar e construção com material 8%/12%; demais
                      32%). A atividade ao lado, se escolhida, vale para toda a
                      receita.
                    </Regra>
                    <Regra tipo="info" titulo="INSS patronal">
                      Fora do Simples (e no Anexo IV): 20% + RAT × FAP +
                      terceiros sobre a folha e 20% sobre o pró-labore.
                    </Regra>
                  </>
                }
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Campo
                    rotulo="Folha de salários no ano"
                    dica="Salários dos empregados."
                  >
                    <EntradaNumero
                      tipo="reais"
                      valor={pl.folhaAnual}
                      min={0}
                      onValor={(v) => mudarPlan({ folhaAnual: v ?? 0 })}
                    />
                  </Campo>
                  <Campo rotulo="Pró-labore no ano" dica="Retirada dos sócios.">
                    <EntradaNumero
                      tipo="reais"
                      valor={pl.proLaboreAnual}
                      min={0}
                      onValor={(v) => mudarPlan({ proLaboreAnual: v ?? 0 })}
                    />
                  </Campo>
                  <Campo
                    rotulo="Outras despesas no ano"
                    dica="Aluguel, energia, serviços, depreciação — só o Lucro Real usa."
                  >
                    <EntradaNumero
                      tipo="reais"
                      valor={pl.despesasOperacionaisAnuais}
                      min={0}
                      onValor={(v) =>
                        mudarPlan({ despesasOperacionaisAnuais: v ?? 0 })
                      }
                    />
                  </Campo>
                  <Campo
                    rotulo="Lucro antes do IR (opcional)"
                    dica="Em branco, o Lucro Real é estimado. Prejuízo: valor negativo."
                  >
                    <EntradaNumero
                      tipo="reais"
                      valor={pl.lucroAntesIrInformado}
                      vazio="estimar"
                      onValor={(v) => mudarPlan({ lucroAntesIrInformado: v })}
                    />
                  </Campo>
                  <div className="sm:col-span-2">
                    <Campo rotulo="Atividade (presunção)">
                      <Selecao
                        value={pl.atividade ?? ""}
                        onChange={(e) =>
                          mudarPlan({
                            atividade: (e.target.value ||
                              null) as PremissasPlanejamento["atividade"],
                          })
                        }
                      >
                        <option value="">
                          Por tipo de receita e perfil de serviço
                        </option>
                        {ATIVIDADES_PRESUNCAO.map((a) => (
                          <option key={a} value={a}>
                            {ROTULO_ATIVIDADE_PRESUNCAO[a]}
                          </option>
                        ))}
                      </Selecao>
                    </Campo>
                  </div>
                  <Campo rotulo="Anexo do Simples">
                    <Selecao
                      value={pl.anexoSimples ?? ""}
                      onChange={(e) =>
                        mudarPlan({
                          anexoSimples: (e.target.value ||
                            null) as PremissasPlanejamento["anexoSimples"],
                        })
                      }
                    >
                      <option value="">Automático (cadastro ou fator R)</option>
                      {(["I", "II", "III", "IV", "V"] as const).map((a) => (
                        <option key={a} value={a}>
                          Anexo {a}
                        </option>
                      ))}
                    </Selecao>
                  </Campo>
                  <Campo rotulo="Risco de acidente (RAT)">
                    <Selecao
                      value={pl.rat}
                      onChange={(e) =>
                        mudarPlan({
                          rat: e.target.value as PremissasPlanejamento["rat"],
                        })
                      }
                    >
                      <option value="LEVE">Leve (1,00%)</option>
                      <option value="MEDIO">Médio (2,00%)</option>
                      <option value="GRAVE">Grave (3,00%)</option>
                    </Selecao>
                  </Campo>
                  <Campo rotulo="FAP" dica="De 0,50 a 2,00.">
                    <EntradaNumero
                      tipo="fator"
                      valor={pl.fap}
                      min={500_000}
                      max={2_000_000}
                      onValor={(v) => mudarPlan({ fap: v ?? 1_000_000 })}
                    />
                  </Campo>
                  <Campo
                    rotulo="Terceiros"
                    dica="Sistema S, salário-educação, INCRA — conforme o FPAS."
                  >
                    <EntradaNumero
                      tipo="percentual"
                      valor={pl.terceiros}
                      min={0}
                      max={1_000_000}
                      onValor={(v) => mudarPlan({ terceiros: v ?? 0 })}
                    />
                  </Campo>
                  <Campo
                    rotulo="LC 224/2025 no Presumido"
                    dica="+10% na presunção acima de R$ 5.000.000,00; há liminares suspendendo."
                  >
                    <Selecao
                      value={pl.aplicarLc224 ? "SIM" : "NAO"}
                      onChange={(e) =>
                        mudarPlan({ aplicarLc224: e.target.value === "SIM" })
                      }
                    >
                      <option value="SIM">Aplicar o acréscimo</option>
                      <option value="NAO">Não aplicar (liminar)</option>
                    </Selecao>
                  </Campo>
                </div>
              </Duas>
            )}

            <label className="mt-4 flex items-start gap-2 border-t border-linha pt-3 text-[13px] text-tinta-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={f.anualizar}
                onChange={(e) => mudar({ anualizar: e.target.checked })}
              />
              <span>
                <strong className="text-tinta">Anualizar</strong> quando houver
                menos de 12 meses de notas (projeta o ano proporcionalmente e
                marca o resultado).
              </span>
            </label>
            {alterado && (
              <p className="mt-2 text-xs text-acao-forte">
                Premissas alteradas: valem ao clicar em{" "}
                {jaRodou ? '"Simular de novo"' : '"Rodar simulação"'}.
              </p>
            )}
          </Cartao>
        ) : (
          <p className="text-xs text-tinta-3">
            Premissas: {resumo.join(" · ")}.{" "}
            <button
              type="button"
              className="text-navy underline"
              onClick={() => setAberto(true)}
            >
              Ver regras e editar
            </button>
            {alterado && (
              <span className="ml-1 text-acao-forte">
                (alteradas, ainda não simuladas)
              </span>
            )}
          </p>
        )}
        {executar.isError && <Aviso>{mensagemDeErro(executar.error)}</Aviso>}
      </>
    ),
  };
}

function Duas({
  regras,
  children,
}: {
  regras: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tinta-3">
          O que a regra decide
        </p>
        <ul className="flex flex-col divide-y divide-linha rounded-md border border-linha">
          {regras}
        </ul>
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-tinta-3">
          O que o escritório escolhe
        </p>
        {children}
      </div>
    </div>
  );
}

const MARCA = {
  entra: { simbolo: "+", classe: "bg-ok/10 text-ok", rotulo: "entra" },
  abate: { simbolo: "−", classe: "bg-navy/10 text-navy", rotulo: "abate" },
  fora: { simbolo: "×", classe: "bg-fundo text-tinta-3", rotulo: "fica fora" },
  info: { simbolo: "i", classe: "bg-fundo text-tinta-2", rotulo: "regra" },
} as const;

function Regra({
  tipo,
  titulo,
  valor,
  children,
}: {
  tipo: keyof typeof MARCA;
  titulo: string;
  valor?: string;
  children: ReactNode;
}) {
  const m = MARCA[tipo];
  return (
    <li className="flex gap-3 px-3 py-2.5">
      <span
        aria-label={m.rotulo}
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded text-xs font-bold ${m.classe}`}
      >
        {m.simbolo}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <p className="text-[13px] font-medium text-tinta">{titulo}</p>
          {valor !== undefined && (
            <p className="num whitespace-nowrap text-[13px] text-tinta-2">
              {valor}
            </p>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-tinta-2">
          {children}
        </p>
      </div>
    </li>
  );
}
